import { MouseEvent } from "react";
import { faCircleQuestion } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { IComponent } from "../../../models/Interfaces";
import { HelpVisualType, resolveComponentHelp } from "./componentHelpCatalog";
import styles from "./designer.module.scss";

interface IComponentHelpPopoverProps {
  component: IComponent;
  isOpen: boolean;
  onToggle: (nextOpen: boolean) => void;
}

interface IVisualGuideProps {
  visualType: HelpVisualType;
}

const VisualGuide = ({ visualType }: IVisualGuideProps): JSX.Element => {
  switch (visualType) {
    case "accordion":
      return (
        <div className={styles.helpVisualStack}>
          <span>Question one</span>
          <span>Question two</span>
          <span>Question three</span>
        </div>
      );
    case "hero":
      return (
        <div className={styles.helpVisualHero}>
          <span>Main headline</span>
          <span>Supporting intro text</span>
        </div>
      );
    case "card":
      return (
        <div className={styles.helpVisualCards}>
          <span>Feature</span>
          <span>Feature</span>
          <span>Feature</span>
        </div>
      );
    case "carousel":
      return (
        <div className={styles.helpVisualCarousel}>
          <div>Slide preview</div>
          <span>1 / 3</span>
        </div>
      );
    case "profile":
      return (
        <div className={styles.helpVisualProfile}>
          <div>Photo</div>
          <p />
        </div>
      );
    case "embed":
    case "video":
      return <div className={styles.helpVisualEmbed}><span>{visualType === "video" ? "Video player" : "Embedded content"}</span></div>;
    case "form":
      return (
        <div className={styles.helpVisualForm}>
          <span>Name</span>
          <span>Email</span>
          <span>Message</span>
          <span>Submit</span>
        </div>
      );
    case "gallery":
      return (
        <div className={styles.helpVisualGallery}>
          <span>Image</span>
          <span>Image</span>
          <span>Image</span>
          <span>Image</span>
        </div>
      );
    case "html":
    case "html-blocks":
      return (
        <div className={styles.helpVisualCode}>
          <span>&lt;section&gt;</span>
          <span>&lt;div&gt;Custom block&lt;/div&gt;</span>
          <span>&lt;/section&gt;</span>
        </div>
      );
    case "image":
      return <div className={styles.helpVisualImage}><span>Image area</span></div>;
    case "menu":
      return (
        <div className={styles.helpVisualMenu}>
          <span>Home</span>
          <span>About</span>
          <span>Services</span>
          <span>Contact</span>
        </div>
      );
    case "news-article":
      return (
        <div className={styles.helpVisualArticle}>
          <span>Article headline</span>
          <span>Author and date</span>
          <span>Lead paragraph</span>
          <span>Read full story...</span>
        </div>
      );
    case "news-summary":
      return (
        <div className={styles.helpVisualSummary}>
          <span>News item title</span>
          <span>News item summary</span>
        </div>
      );
    case "subscribe":
      return (
        <div className={styles.helpVisualSubscribe}>
          <span>Join updates</span>
          <span>Email address</span>
          <span>Subscribe</span>
        </div>
      );
    case "social":
      return (
        <div className={styles.helpVisualSocial}>
          <span>Facebook</span>
          <span>Instagram</span>
          <span>X / LinkedIn</span>
        </div>
      );
    case "tabs":
      return (
        <div className={styles.helpVisualTabs}>
          <p>
            <span>Tab 1</span>
            <span>Tab 2</span>
            <span>Tab 3</span>
          </p>
          <div>Active tab content</div>
        </div>
      );
    case "text-block":
      return (
        <div className={styles.helpVisualText}>
          <span>Section title</span>
          <span>Body paragraph one</span>
          <span>Body paragraph two</span>
        </div>
      );
    default:
      return (
        <div className={styles.helpVisualGeneric}>
          <span>Component title</span>
          <span>Configurable content</span>
        </div>
      );
  }
};

const stopEvent = (event: MouseEvent<HTMLElement>): void => {
  event.preventDefault();
};

const ComponentHelpPopover = ({ component, isOpen, onToggle }: IComponentHelpPopoverProps): JSX.Element => {
  const helpMeta = resolveComponentHelp(component);

  return (
    <OverlayTrigger
      trigger="click"
      placement="right"
      rootClose
      show={isOpen}
      onToggle={onToggle}
      overlay={
        <Popover id={`component-help-${component.Id}`} className={`shadow ${styles.componentHelpPopover}`}>
          <Popover.Body className={styles.componentHelpBody}>
            <div className={styles.componentHelpTitle}>{helpMeta.title}</div>
            <div className={styles.componentHelpSummary}>{helpMeta.summary}</div>
            <div className={styles.componentHelpBestFor}>
              <strong>Best for:</strong> {helpMeta.bestFor}
            </div>
            <div className={styles.componentHelpVisualLabel}>Quick visual guide</div>
            <VisualGuide visualType={helpMeta.visualType} />
          </Popover.Body>
        </Popover>
      }
    >
      <button
        type="button"
        draggable={false}
        className={styles.componentHelpTrigger}
        title={`About ${helpMeta.title}`}
        aria-label={`About ${helpMeta.title}`}
        onClick={stopEvent}
        onMouseDown={stopEvent}
      >
        <FontAwesomeIcon icon={faCircleQuestion} />
      </button>
    </OverlayTrigger>
  );
};

export default ComponentHelpPopover;
